import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './contexts/LanguageContext';
import './ExplorationPage.css';

const ExplorationPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // 以话题为单位组织数据
  const topics = [
    {
      id: 1,
      title: language === 'en' ? 'GPT-5 Global Release' : 'GPT-5全球发布',
      source: language === 'en' ? 'OpenAI Official Statement - Tech Daily' : 'OpenAI官方声明 - 科技日报',
      likes: 156,
      tags: language === 'en' ? ['Technology', 'AI', 'Education'] : ['科技', 'AI', '教育'],
      parts: {
        part1: {
          questions: [
            'Do you often use AI tools in your daily life?',
            'What kind of AI technology do you find most useful?',
            'Have you ever tried ChatGPT or similar tools?',
            'Do you think AI will make our lives easier in the future?',
            'Should children be taught how to use AI at school?',
            'What jobs do you think AI can\'t replace?',
            'How has AI changed the way people work?',
            'Would you trust AI to give you medical advice?'
          ]
        },
        part2: {
          question: 'Describe a time when you used AI technology.',
          cues: [
            'What the technology was',
            'How you used it',
            'And how you felt about it'
          ]
        },
        part3: {
          questions: [
            'Do you think AI will replace human jobs?',
            'Should governments regulate AI development?',
            'How can AI improve education systems?'
          ]
        }
      }
    },
    {
      id: 2,
      title: language === 'en' ? 'Long-acting HIV Vaccine Breakthrough' : '长效HIV疫苗问世',
      source: language === 'en' ? 'WHO Annual Medical Research Report - Health News' : 'WHO年度医疗研究报告 - 卫生报',
      likes: 128,
      tags: language === 'en' ? ['Medical', 'Technology', 'Ethics'] : ['医疗', '科技', '伦理'],
      parts: {
        part1: {
          questions: [
            'How important do you think vaccines are?',
            'Have you ever participated in any medical research?',
            'Do you regularly check your health status?',
            'What do you think about free healthcare?',
            'Should governments spend more on medical research?',
            'Have you ever donated blood or organs?',
            'What\'s the most impressive medical breakthrough you\'ve heard of?',
            'Do you think all countries should share medical technologies?'
          ]
        },
        part2: {
          question: 'Describe an important medical breakthrough you know about.',
          cues: [
            'What the breakthrough was',
            'How it works',
            'And why it is significant'
          ]
        },
        part3: {
          questions: [
            'Should healthcare be free for everyone?',
            'How can governments encourage medical research?',
            'What are the ethical issues in vaccine testing?'
          ]
        }
      }
    },
    {
      id: 3,
      title: language === 'en' ? 'Japan Nuclear Wastewater Discharge Controversy' : '日本核污水排放争议',
      source: language === 'en' ? 'Environmental Organizations Joint Statement - Global Times' : '环保组织联合声明 - 环球时报',
      likes: 142,
      tags: language === 'en' ? ['Environment', 'International Affairs', 'Current Events'] : ['环境', '国际事务', '时事'],
      parts: {
        part1: {
          questions: [
            'Is water pollution a problem in your country?',
            'How do you usually get your drinking water?',
            'Have you ever participated in any environmental protection activities?',
            'What\'s the most serious environmental issue in your opinion?',
            'Do you think individuals can help solve environmental problems?',
            'Should companies be punished for causing pollution?',
            'How often do you recycle?',
            'What can schools do to teach children about environmental protection?'
          ]
        },
        part2: {
          question: 'Describe an environmental problem in your country.',
          cues: [
            'What the problem is',
            'What causes it',
            'And how it could be solved'
          ]
        },
        part3: {
          questions: [
            'Who should be responsible for protecting the environment?',
            'Is international cooperation necessary for environmental issues?',
            'How does pollution affect marine life?'
          ]
        }
      }
    },
    {
      id: 4,
      title: language === 'en' ? 'First Human Brain-Computer Interface Implantation' : '人类首例脑机接口植入',
      source: language === 'en' ? 'Neuralink Human Trial Success - Science Weekly' : 'Neuralink人体试验成功 - 科学周刊',
      likes: 115,
      tags: language === 'en' ? ['Technology', 'Medical', 'Ethics'] : ['科技', '医疗', '伦理'],
      parts: {
        part1: {
          questions: [
            'Are you interested in new technologies?',
            'What futuristic technology excites you most?',
            'Would you try a brain implant if it could improve your memory?',
            'Do you think technology is developing too fast?',
            'What modern invention do you think is most important?',
            'Should there be limits on human enhancement technologies?',
            'How often do you read about scientific breakthroughs?',
            'Do you think humans will merge with machines in the future?'
          ]
        },
        part2: {
          question: 'Describe a piece of futuristic technology you find interesting.',
          cues: [
            'What the technology is',
            'How it might be used',
            'And why it interests you'
          ]
        },
        part3: {
          questions: [
            'Should there be limits on human enhancement technologies?',
            'What ethical problems might brain implants cause?',
            'Will humans and machines merge in the future?'
          ]
        }
      }
    },
    {
      id: 5,
      title: language === 'en' ? 'Bitcoin Halving Event' : '比特币减半事件',
      source: language === 'en' ? 'Global Cryptocurrency Market Analysis - Financial Times' : '全球加密货币市场分析 - 金融时报',
      likes: 98,
      tags: language === 'en' ? ['Finance', 'Technology', 'Blockchain'] : ['金融', '科技', '区块链'],
      parts: {
        part1: {
          questions: [
            'Do you often use digital payments?',
            'What do you think about cryptocurrencies?',
            'Have you ever invested in Bitcoin or other digital currencies?',
            'Do you think cash will disappear in the future?',
            'What are the advantages of digital payments?',
            'Should governments regulate cryptocurrencies?',
            'How do you usually save money?',
            'What\'s the biggest risk of using digital currencies?'
          ]
        },
        part2: {
          question: 'Describe a trend in the economy you have noticed.',
          cues: [
            'What the trend is',
            'How it affects people',
            'And whether you think it is positive'
          ]
        },
        part3: {
          questions: [
            'Is cash still important in your country?',
            'What are the risks of digital currencies?',
            'How might blockchain change the financial system?'
          ]
        }
      }
    },
    {
      id: 6,
      title: language === 'en' ? 'Metaverse Virtual Real Estate Boom' : '元宇宙虚拟房产热潮',
      source: language === 'en' ? 'Digital Economy Development Report - Internet Weekly' : '数字经济发展报告 - 互联网周刊',
      likes: 87,
      tags: language === 'en' ? ['Technology', 'Society', 'Virtual Reality'] : ['科技', '社会', '虚拟现实'],
      parts: {
        part1: {
          questions: [
            'Do you spend much time in virtual worlds or games?',
            'Have you ever purchased anything virtual with real money?',
            'Do you think virtual property has real value?',
            'How has the internet changed the concept of ownership?',
            'Would you consider investing in virtual real estate?',
            'Should there be laws protecting virtual property?',
            'How might metaverse affect social interactions?',
            'Do you think people will work in virtual worlds in the future?'
          ]
        },
        part2: {
          question: 'Describe a digital purchase you made that was meaningful to you.',
          cues: [
            'What you purchased',
            'Why you bought it',
            'And how it affected your life'
          ]
        },
        part3: {
          questions: [
            'Will virtual property become as valuable as physical property in the future?',
            'How might metaverse change the real estate industry?',
            'What are the potential risks of investing in virtual assets?'
          ]
        }
      }
    }
  ];

  // 点击卡片进入详情页的处理函数
  const handleTopicClick = (topicId) => {
    navigate(`/topic/${topicId}`);
  };

  return (
    <div className="exploration-page">
      <div className="exploration-container">
        <div className="exploration-header">
          <h1>{language === 'en' ? 'Popular Predicted Topics' : '热门预测话题'}</h1>
          <p className="exploration-subtitle">
            {language === 'en' 
              ? 'Click on topic cards to view details, practice speaking or collect drift bottles' 
              : '点击话题卡片查看详情，练习口语或收集漂流瓶'}
          </p>
        </div>
        
        <div className="topics-container">
          {topics.map((topic) => (
            <div 
              key={topic.id} 
              className="topic-item"
              onClick={() => handleTopicClick(topic.id)}
            >
              <div className="topic-title">{topic.title}</div>
              <div className="topic-preview">{topic.parts.part3.questions[0]}</div>
              <div className="topic-source">{language === 'en' ? 'Source: ' : '来源: '}{topic.source}</div>
              <div className="topic-tags">
                {topic.tags.map((tag, index) => (
                  <span key={index} className={`topic-tag tag-${language === 'en' ? tag.toLowerCase() : tag}`}>{tag}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExplorationPage; 